/**
 * FieldDrawingForm — bottom sheet shown after user draws a polygon on the map.
 * Collects field metadata and POSTs to /api/v1/aif/farm/fields.
 *
 * Props:
 *   polygon    — GeoJSON Polygon geometry object (from leaflet-draw)
 *   token      — JWT
 *   onSave     — (newField) => void — called after successful save
 *   onCancel   — () => void
 */
import { useState, useId, useEffect, useRef } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';

const SOIL_TYPE_KEYS = [
  { value: '', i18nKey: 'field.soilTypeSelect' },
  { value: 'loam',        i18nKey: 'field.soilLoam' },
  { value: 'sandy_loam',  i18nKey: 'field.soilSandyLoam' },
  { value: 'clay_loam',   i18nKey: 'field.soilClayLoam' },
  { value: 'clay',        i18nKey: 'field.soilClay' },
  { value: 'sand',        i18nKey: 'field.soilSand' },
  { value: 'silt',        i18nKey: 'field.soilSilt' },
  { value: 'peat',        i18nKey: 'field.soilPeat' },
];

const CROP_KEYS = [
  { value: '', i18nKey: 'field.cropNone' },
  { value: 'wheat',     i18nKey: 'field.cropWheat' },
  { value: 'barley',    i18nKey: 'field.cropBarley' },
  { value: 'rapeseed',  i18nKey: 'field.cropRapeseed' },
  { value: 'corn',      i18nKey: 'field.cropCorn' },
  { value: 'potato',    i18nKey: 'field.cropPotato' },
  { value: 'sugarbeet', i18nKey: 'field.cropSugarbeet' },
  { value: 'grassland', i18nKey: 'field.cropGrassland' },
  { value: 'sunflower', i18nKey: 'field.cropSunflower' },
  { value: 'peas',      i18nKey: 'field.cropPeas' },
  { value: 'fababean',  i18nKey: 'field.cropFababean' },
  { value: 'other',     i18nKey: 'field.cropOther' },
];

export default function FieldDrawingForm({ polygon, token, onSave, onCancel }) {
  const { t } = useTranslation();
  const titleId = useId();
  const firstInputRef = useRef(null);

  // Auto-focus first input on mount
  useEffect(() => {
    if (firstInputRef.current) firstInputRef.current.focus();
  }, []);

  // Escape key closes form
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  const [name, setName] = useState('');
  const [crop, setCrop] = useState('');
  const [sowingYear, setSowingYear] = useState(new Date().getFullYear());
  const [soilType, setSoilType] = useState('');
  const [soilQualityIndex, setSoilQualityIndex] = useState('');
  const [nitrateZone, setNitrateZone] = useState(false);
  const [organic, setOrganic] = useState(false);
  const [pachtRate, setPachtRate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!name.trim()) { setError(t('field.nameRequired')); return; }
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        polygon_geojson: polygon,
        area_ha: 0.1, // backend will compute real area from polygon
      };
      if (crop) body.current_crop = crop;
      if (sowingYear) body.sowing_year = parseInt(sowingYear, 10);
      if (soilType) body.soil_type = soilType;
      if (soilQualityIndex) body.soil_quality_index = parseInt(soilQualityIndex, 10);
      if (nitrateZone) body.nitrate_zone = true;
      if (organic) body.organic_field = true;
      if (pachtRate) body.pacht_rate_eur_ha = parseFloat(pachtRate);
      if (notes.trim()) body.notes = notes.trim();

      const newField = await apiClient.post('/api/v1/aif/farm/fields', body, { queueOnOffline: true });
      onSave?.(newField);
    } catch (e) {
      setError(e.body?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="field-drawing-form"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className="absolute bottom-0 left-0 right-0 z-[var(--aif-z-bottom-sheet)] bg-[var(--aif-bg-surface)] rounded-t-2xl
                 shadow-2xl border-t border-[var(--aif-border-default)] max-h-[80vh] overflow-y-auto"
    >
      {/* Drag handle */}
      <div className="flex justify-center pt-2 pb-1 sticky top-0 bg-[var(--aif-bg-surface)]" aria-hidden="true">
        <div className="w-8 h-1 rounded-full bg-[var(--aif-border-default)]" />
      </div>

      <div className="px-4 pb-6 space-y-4">
        <h2 id={titleId} className="text-base font-semibold text-[var(--aif-text-primary)]">
          {t('field.createNew')}
        </h2>

        {error && (
          <div className="text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* Name */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">
            {t('form.fieldName')} *
          </label>
          <input
            ref={firstInputRef}
            data-testid="field-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('field.namePlaceholder')}
            className="w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          />
        </div>

        {/* Crop */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('form.currentCrop')}</label>
          <select
            data-testid="field-crop-select"
            value={crop}
            onChange={(e) => setCrop(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          >
            {CROP_KEYS.map(c => <option key={c.value} value={c.value}>{t(c.i18nKey)}</option>)}
          </select>
        </div>

        {/* Sowing year */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('field.sowingYear')}</label>
          <input
            type="number"
            min="2020"
            max="2040"
            value={sowingYear}
            onChange={(e) => setSowingYear(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          />
        </div>

        {/* Soil type */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('form.soilType')}</label>
          <select
            data-testid="field-soil-type-select"
            value={soilType}
            onChange={(e) => setSoilType(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          >
            {SOIL_TYPE_KEYS.map(s => <option key={s.value} value={s.value}>{t(s.i18nKey)}</option>)}
          </select>
        </div>

        {/* Soil quality index (Bodenpunktzahl) */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">
            {t('field.soilQualityRange')}
          </label>
          <input
            data-testid="field-soil-quality-input"
            type="number"
            min="1"
            max="100"
            value={soilQualityIndex}
            onChange={(e) => setSoilQualityIndex(e.target.value)}
            placeholder={t('field.soilQualityPlaceholder')}
            className="w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          />
        </div>

        {/* Flags */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[var(--aif-text-primary)] cursor-pointer">
            <input
              type="checkbox"
              checked={nitrateZone}
              onChange={(e) => setNitrateZone(e.target.checked)}
              className="rounded"
            />
            {t('field.nitrateZone')}
          </label>
          <label className="flex items-center gap-2 text-sm text-[var(--aif-text-primary)] cursor-pointer">
            <input
              type="checkbox"
              checked={organic}
              onChange={(e) => setOrganic(e.target.checked)}
              className="rounded"
            />
            {t('field.organicFarm')}
          </label>
        </div>

        {/* Pacht */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('field.leaseRateOptional')}</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={pachtRate}
            onChange={(e) => setPachtRate(e.target.value)}
            placeholder={t('field.leaseRatePlaceholder')}
            className="w-full px-3 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs text-[var(--aif-text-muted)] mb-1">{t('field.notesOptional')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('field.notesPlaceholder')}
            rows={2}
            className="w-full px-3 py-2 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)] resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            data-testid="field-form-cancel"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       text-[var(--aif-text-secondary)] text-sm font-medium
                       hover:bg-[var(--aif-bg-elevated)] transition disabled:opacity-50"
          >
            {t('form.cancel')}
          </button>
          <button
            data-testid="field-form-save"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex-1 py-2.5 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium
                       hover:opacity-90 transition disabled:opacity-50"
          >
            {saving ? t('form.saving') : t('field.saveField')}
          </button>
        </div>
      </div>
    </div>
  );
}
